import { useState, useMemo, useRef } from 'react'
import { icons } from 'lucide-react'
import curatedIconNames from '../../../../../config/tag-icons.json'
import styles from './IconPicker.module.css'
import { useDismissOnOutside } from '../hooks/useDismissOnOutside'

const LABEL_WORD_INITIAL_PATTERN = /(^|[-_])(\w)/g

const toPascalCase = (name) => name.replace(LABEL_WORD_INITIAL_PATTERN, (_, _separator, character) => character.toUpperCase())

const ICON_ENTRIES = curatedIconNames
  .map((kebab) => ({ pascal: toPascalCase(kebab), kebab }))
  .filter(({ pascal }) => icons[pascal])

function IconPicker({ value, onChange, searchInputId, searchLabel = 'Search icons' }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  const filtered = useMemo(() => {
    if (!query.trim()) return ICON_ENTRIES.slice(0, 40)
    const q = query.toLowerCase()
    return ICON_ENTRIES.filter((e) => e.kebab.includes(q)).slice(0, 40)
  }, [query])

  useDismissOnOutside([containerRef], () => setOpen(false), { enabled: open })

  const selectedPascal = value ? toPascalCase(value) : null
  const SelectedIcon = selectedPascal ? icons[selectedPascal] : null

  const selectIcon = (entry) => {
    onChange(entry.kebab)
    setOpen(false)
    setQuery('')
  }

  const clear = () => {
    onChange('')
    setQuery('')
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.inputRow}>
        {SelectedIcon && (
          <span className={styles.selectedPreview}>
            <SelectedIcon size={16} />
          </span>
        )}
        <input
          id={searchInputId}
          aria-label={searchLabel}
          className={styles.input}
          placeholder="Search icons..."
          value={open ? query : value || ''}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!open) setOpen(true)
          }}
          onFocus={() => {
            setOpen(true)
            setQuery('')
          }}
        />
        {value && (
          <button type="button" className={styles.clearBtn} onClick={clear}>
            ×
          </button>
        )}
      </div>

      {open && (
        <div className={styles.dropdown}>
          {filtered.length === 0 ? (
            <div className={styles.noResults}>No icons found</div>
          ) : (
            <div className={styles.grid}>
              {filtered.map((entry) => {
                const Icon = icons[entry.pascal]
                return (
                  <button
                    key={entry.pascal}
                    type="button"
                    className={`${styles.iconBtn} ${value === entry.kebab ? styles.active : ''}`}
                    onClick={() => selectIcon(entry)}
                    title={entry.kebab}
                  >
                    <Icon size={18} />
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default IconPicker
